import { useEffect, useRef, useState } from "react";
import { FaAward } from "react-icons/fa6";

// Icons
const SolarIcon = () => (
  <svg viewBox="0 0 40 40" width="32" height="32" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="18" width="32" height="16" rx="2" />
    <path d="M4 24h32M12 18v16M20 18v16M28 18v16" />
    <circle cx="30" cy="10" r="4" />
    <path d="M30 6V4M30 16v2M26 10h-2M34 10h2M27.2 7.2l-1.4-1.4M33.2 13.2l1.4 1.4M27.2 12.8l-1.4 1.4M33.2 7.2l1.4-1.4" strokeWidth="1.3" />
  </svg>
);

const ClientIcon = () => (
  <svg viewBox="0 0 40 40" width="32" height="32" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6c-3 0-5 2-5 4.5S17 15 20 18c3-3 5-5 5-7.5S23 6 20 6z" />
    <path d="M8 26c0-3 2.5-5 6-5M32 26c0-3-2.5-5-6-5" />
    <circle cx="9" cy="17" r="3.5" />
    <circle cx="31" cy="17" r="3.5" />
    <path d="M11 34c0-4 3-6.5 9-6.5s9 2.5 9 6.5" />
  </svg>
);

const AwardIcon = () => (
  <svg viewBox="0 0 40 40" width="32" height="32" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="20" cy="16" r="10" />
    <path d="M20 10l1.8 5.2 5.5.8-4 3.9 1 5.4L20 22.8l-4.3 2.5 1-5.4-4-3.9 5.5-.8z" />
    <path d="M14 24l-3 10 4.5-2 2.5 4L20 28M26 24l3 10-4.5-2-2.5 4L20 28" />
  </svg>
);

const RatingIcon = () => (
  <svg viewBox="0 0 40 40" width="32" height="32" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="22" width="28" height="14" rx="3" />
    <path d="M12 22v-4a8 8 0 0116 0v4" />
    <circle cx="14" cy="30" r="2.5" />
    <circle cx="26" cy="30" r="2.5" />
    <path d="M20 8l1.2 3.6H25l-3 2.2 1.1 3.6L20 15.3l-3.1 2.1 1.1-3.6-3-2.2h3.8z" strokeWidth="1.4" />
  </svg>
);

const stats = [
  { id: 1, Icon: SolarIcon,  target: 300, suffix: "+", label: "Project Done" },
  { id: 2, Icon: ClientIcon, target: 300, suffix: "+", label: "Happy Clients" },
  { id: 3, Icon: AwardIcon ,  target: 50,  suffix: "+", label: "Area Cover" },
  { id: 4, Icon: RatingIcon, target: 300, suffix: "+", label: "Rating Customer" },
];

// Counter hook
function useCounter(target, duration = 2000, started = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!started) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return count;
}

// Individual stat with counter
function StatItem({ Icon, target, suffix, label, started }) {
  const count = useCounter(target, 2000, started);

  // Format with comma: 1000 → 1,000
  const formatted = count.toLocaleString();

  return (
    <div className="flex items-center gap-4 flex-1">
      {/* Green icon box */}
      <div
        className="flex-shrink-0 flex items-center justify-center bg-[#89EA5F] rounded-2xl"
        style={{
          width: "70px",
          height: "70px",
        }}
      >
        <Icon  />
      </div>

      {/* Number + label */}
      <div>
        <p
          className="font-black leading-none mb-1 text-white"
          style={{ fontSize: "clamp(22px, 2.2vw, 30px)", letterSpacing: "-0.01em" }}
        >
          {formatted}{suffix}
        </p>
        <p className="text-sm font-semibold mt-2" style={{ color: "#86e55c" }}>{label}</p>
      </div>
    </div>
  );
}

export default function StatsBanner() {
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  // Start counter when section enters viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="w-full py-16 px-8"
      style={{ background: "#0d2d0d", fontFamily: "'Barlow', 'Montserrat', sans-serif" }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map(({ id, Icon, target, suffix, label }) => (
            <StatItem
              key={id}
              Icon={Icon}
              target={target}
              suffix={suffix}
              label={label}
              started={started}
            />
          ))}
        </div>
      </div>
    </div>
  );
}